/**
 * MobileBottomSheet.js
 * 모바일 뷰용 하단 시트 (Bottom Sheet) 컴포넌트
 * 시간 블록 생성/수정 폼
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  InputAdornment,
  Divider,
  Slide,
  Backdrop,
} from '@mui/material';
import { X, Search, Mic } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const MobileBottomSheet = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  contracts = [],
  suggestions = [],
  onSearchContracts,
  title = '시간 블록 입력',
}) => {
  const { colors, isDark } = useCustomTheme();
  const [formData, setFormData] = useState({
    contract: null,
    work_description: '',
    input_minute: 60,
    input_description: '',
    log_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        contract: null,
        work_description: '',
        input_minute: 60,
        input_description: '',
        log_date: new Date().toISOString().split('T')[0],
      });
    }
  }, [initialData, open]);

  const handleSubmit = () => {
    if (!formData.contract) {
      alert('프로젝트를 선택해주세요.');
      return;
    }
    if (!formData.work_description) {
      alert('업무 설명을 입력해주세요.');
      return;
    }
    
    const submitData = {
      log_date: formData.log_date,
      contract_id: formData.contract.contract_id,
      company_code: formData.contract.company_code,
      work_description: formData.work_description,
      input_minute: parseInt(formData.input_minute) || 60,
      input_description: formData.input_description || null,
    };
    
    onSubmit(submitData);
    onClose();
  };

  return (
    <>
      <Backdrop
        open={open}
        onClick={onClose}
        sx={{
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      />
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1301,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '90vh',
            overflow: 'auto',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
            touchAction: 'pan-y', // 수직 스크롤만 허용
            backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* 헤더 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: `1px solid ${colors.primaryBorder}`,
              position: 'sticky',
              top: 0,
              backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
              zIndex: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primaryText, fontSize: '14px' }}>
              {title}
            </Typography>
            <IconButton onClick={onClose} size="large">
              <X size={24} />
            </IconButton>
          </Box>

          {/* 폼 내용 */}
          <Box sx={{ p: 2 }}>
            {/* 업무일자 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.secondaryText, fontWeight: 600, fontSize: '12px' }}>
                업무일자
              </Typography>
              <TextField
                type="date"
                value={formData.log_date}
                onChange={(e) => setFormData(prev => ({ ...prev, log_date: e.target.value }))}
                fullWidth
                size="large"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '12px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '12px',
                  },
                }}
              />
            </Box>

            {/* 업체/계약 검색 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.secondaryText, fontWeight: 600, fontSize: '12px' }}>
                업체/계약 검색
              </Typography>
              <Autocomplete
                options={contracts}
                getOptionLabel={(option) => 
                  option.display_label || `${option.company_name_kr || ''} - ${option.contract_title || option.contract_id || ''}`
                }
                value={formData.contract}
                onChange={(_, newValue) => {
                  setFormData(prev => ({ ...prev, contract: newValue }));
                  if (newValue && onSearchContracts) {
                    onSearchContracts(newValue.contract_id);
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  if (onSearchContracts && newInputValue.length >= 2) {
                    onSearchContracts(newInputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="업체명 또는 계약명을 입력하세요"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '12px',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '12px',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={20} style={{ color: colors.secondaryText }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                fullWidth
                size="large"
              />
            </Box>

            {/* 업무 설명 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.secondaryText, fontWeight: 600, fontSize: '12px' }}>
                업무 설명
              </Typography>
              <TextField
                value={formData.work_description}
                onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
                placeholder="예: AEO 인증 컨설팅"
                fullWidth
                size="large"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '12px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '12px',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" sx={{ color: colors.accent }}>
                        <Mic size={18} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* 스마트 제안 */}
              {suggestions.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {suggestions.slice(0, 3).map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outlined"
                      size="small"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        work_description: suggestion.work_description,
                        input_minute: Math.round(suggestion.avg_minutes || 60)
                      }))}
                      sx={{ 
                        mr: 1, 
                        mb: 1,
                        fontSize: '12px',
                        textTransform: 'none',
                      }}
                    >
                      {suggestion.work_description} ({Math.round(suggestion.avg_minutes || 0)}분)
                    </Button>
                  ))}
                </Box>
              )}
            </Box>

            {/* 투입시간 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.secondaryText, fontWeight: 600, fontSize: '12px' }}>
                투입시간
              </Typography>
              <TextField
                type="number"
                value={formData.input_minute}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || value === null || value === undefined) {
                    setFormData(prev => ({ ...prev, input_minute: '' }));
                    return;
                  }
                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue) && numValue > 0) {
                    setFormData(prev => ({ ...prev, input_minute: numValue.toString() }));
                  }
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (isNaN(value) || value <= 0) {
                    setFormData(prev => ({ ...prev, input_minute: '1' }));
                  }
                }}
                inputProps={{
                  min: 1,
                  step: 1,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '12px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '12px',
                  },
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">분</InputAdornment>,
                }}
                fullWidth
                size="large"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 취소 | 저장 버튼 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                fullWidth
                size="large"
                sx={{
                  borderColor: colors.primaryBorder,
                  color: colors.primaryText,
                  minHeight: '56px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                취소
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth
                size="large"
                sx={{
                  backgroundColor: colors.accent,
                  minHeight: '56px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                저장
              </Button>
            </Box>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default MobileBottomSheet;

